<script setup lang="ts">
    import { onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
    import { getDataTableScrollContainer, SCROLL_EDGE_TOLERANCE_PX } from '@/js/utils/dataTableDom';

    const props = defineProps<{
        containerRef: HTMLElement | null;
    }>();

    const FADE_HEIGHT_PX = 52;

    const position = reactive({ top: 0, left: 0, width: 0 });
    const visible = ref(false);
    const hasMoreBelow = ref(false);

    let scrollContainer: HTMLElement | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let wrapperObserver: MutationObserver | null = null;

    const updatePosition = () => {
        const wrapper = props.containerRef;
        const container = getDataTableScrollContainer(props.containerRef);
        if (!wrapper || !container) {
            visible.value = false;
            return;
        }

        const wrapperRect = wrapper.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // containerRect.bottom is the outer border-box edge, which includes the
        // horizontal scrollbar's own track. clientHeight excludes it, so anchoring
        // there keeps the fade over the last rows instead of over the scrollbar.
        const contentBottom = containerRect.top + container.clientHeight;

        position.top = contentBottom - wrapperRect.top - FADE_HEIGHT_PX;
        position.left = containerRect.left - wrapperRect.left;
        // clientWidth excludes the vertical scrollbar's own track by definition,
        // so it never gets faded out along with the rows.
        position.width = container.clientWidth;
        visible.value = true;
    };

    // Visible from the start whenever there are more rows below, not only once
    // already scrolled: the point is to hint that the list continues before
    // the user has scrolled at all.
    const updateScrollState = () => {
        if (!scrollContainer) {
            hasMoreBelow.value = false;
            return;
        }
        hasMoreBelow.value =
            scrollContainer.scrollTop + scrollContainer.clientHeight <
            scrollContainer.scrollHeight - SCROLL_EDGE_TOLERANCE_PX;
    };

    const refresh = () => {
        updatePosition();
        updateScrollState();
    };

    const attachScrollListener = () => {
        scrollContainer?.removeEventListener('scroll', updateScrollState);
        scrollContainer = getDataTableScrollContainer(props.containerRef);
        scrollContainer?.addEventListener('scroll', updateScrollState);
        updateScrollState();
    };

    const attachResizeObserver = () => {
        resizeObserver?.disconnect();
        resizeObserver = null;

        if (!props.containerRef) return;

        resizeObserver = new ResizeObserver(refresh);
        resizeObserver.observe(props.containerRef);
    };

    const attachAll = () => {
        updatePosition();
        attachScrollListener();
        attachResizeObserver();
    };

    onMounted(() => {
        window.addEventListener('resize', updatePosition);
    });

    onBeforeUnmount(() => {
        scrollContainer?.removeEventListener('scroll', updateScrollState);
        window.removeEventListener('resize', updatePosition);
        resizeObserver?.disconnect();
        wrapperObserver?.disconnect();
    });

    // A template ref is null on the render that first passes it as a prop;
    // watch+immediate catches it turning real.
    watch(
        () => props.containerRef,
        (container) => {
            attachAll();

            wrapperObserver?.disconnect();
            wrapperObserver = null;
            if (container) {
                // The wrapper itself is never replaced, so no reattachment needed later.
                wrapperObserver = new MutationObserver(attachAll);
                wrapperObserver.observe(container, { childList: true, subtree: true });
            }
        },
        { immediate: true }
    );
</script>
<template>
    <div
        v-if="visible"
        aria-hidden="true"
        class="absolute z-30 pointer-events-none transition-opacity duration-200"
        :class="hasMoreBelow ? 'opacity-100' : 'opacity-0'"
        :style="{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
            height: `${FADE_HEIGHT_PX}px`,
            backgroundImage:
                'linear-gradient(to top, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)',
        }"
    />
</template>
